import { Box } from "@mui/material";
import { useTimeoutTimer } from "@src/appV2/lib/utils/useTimeoutTimer";
import { Text } from "@src/appV2/redesign/system/ui-components";

interface FullWidthCountdownClockProps {
  endTime: Date;
}

// Time unit constants for readability and maintainability
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;
const SECONDS_PER_DAY = SECONDS_PER_HOUR * 24;

export function FullWidthCountdownClock(props: FullWidthCountdownClockProps) {
  const { endTime } = props;

  const { secondsLeft } = useTimeoutTimer({ initialEndTime: endTime.getTime() });

  // Clamp to avoid negative values when endTime is in the past
  const totalSeconds = Math.max(0, Math.floor(secondsLeft));

  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  return (
    <Box
      display="flex"
      justifyContent="space-around"
      alignItems="center"
      padding={4}
      sx={(theme) => ({
        textAlign: "center",
        gap: { xs: 1, sm: 2 },
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.grey[500]}`,
      })}
    >
      <TimeUnit value={days} label="Days" />
      <Separator />
      <TimeUnit value={hours} label="Hours" />
      <Separator />
      <TimeUnit value={minutes} label="Minutes" />
      <Separator />
      <TimeUnit value={seconds} label="Seconds" />
    </Box>
  );
}

function TimeUnit({ value, label }: { value: number; label: string }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Text bold variant="h3">
        {String(value).padStart(2, "0")}
      </Text>
      <Text variant="caption">{label}</Text>
    </Box>
  );
}

function Separator() {
  return (
    <Text bold variant="h3" sx={{ alignSelf: "flex-start" }}>
      :
    </Text>
  );
}
